import { GridSettingsHelper } from '@bimser/common';
import { BCExtremeGridEntities, findMessage } from '@bimser/components';
import { IDictionary, isNullOrUndefined } from '@bimser/core';
import IconAction from '@bimser/icons/16/action';
import GetIcon16ByName from '@bimser/icons/lib/getIcon16ByName';
import React from 'react';
import debounce from 'lodash/debounce';
import { onBeforeToolbarItemClick } from '.';
import DataGrid from '..';
import {
	IBaseExecuteActionArgs,
	ParameterItemType
} from '../../../../../entities/common/executeActionArgs';
import { geti18nValue } from '../../../../../helper';
import { DataGridSummaryType } from '../entities';

const emptyArray = [];

function getDataGridProps(this: DataGrid): BCExtremeGridEntities.IExtremeGridProps {
	const uniqueDataField = this.getUniqueDataField();
	const totalItems: { columnName: string; type: DataGridSummaryType; suffix: string }[] =
		this.columns?.reduce(
			(result, column) =>
				column.summaryTypePrefixes
					? [
							...result,
							...column.summaryTypePrefixes?.map(s => ({
								columnName: column.name,
								type: s.type,
								suffix: s.suffix || null
							}))
					  ]
					: result,
			[]
		);

	return {
		...getDataGridCommonProps.call(this),
		gridId: `runtime_${this.__form.projectName}_${this.__form.formName}_${this.__form.activeView}_${this.name}`,
		uid: this.__gridId,
		remoteOperations:
			(this.dataSource?.name && !this.dataSource.runAtServer) ||
			(this.__form.documentId > 0 && this.saveMode == 'self'),
		dataSource: this.getDatasourceUpdate(this.rows),
		columns: this.__columns,
		name: this.name,
		columnMinWidth: 60,
		uniqueDataField,
		columnFixing: {
			enabled: this.columnFixingEnabled
		},
		summary: {
			totalItems: totalItems.length ? totalItems : emptyArray
		},
		toolbarItems: getToolbarButtons.call(this),
		languageOptions: {
			currentLanguage: this.__form.currentLanguage,
			supportedLanguages: this.__form.supportedLanguages
		},
		readOnly: this.__isReadOnly?.() || false,
		disabled: this.__isDisabled?.() || false,
		imageClassName: `${this.__form.projectName}_${this.__form.formName}_${this.name}`,
		cacheSettingsEnabled: this.cacheSettingsEnabled,
		onDoubleClickRow: this.onRowDoubleClick,
		onRowRemoving: this.onRowRemoving,
		onRowRemoved: this.onRowRemoved,
		onRowUpdating: this.onRowUpdating,
		onRowUpdated: this.onRowUpdated,
		onRowInserting: this.onRowInserting,
		onRowInserted: this.onRowInserted,
		onSelectionChange: this.onSelectionChange,
		onSelectionChanging: this.onSelectionChanging,
		onCommitChanges: this.onCommitChanges,
		onColumnResizeChange: this.onColumnResizeChange,
		onGridUpdated: this.onGridUpdated,
		onSortingChange: this.onSortingChange,
		onSearchingChange: this.onSearchingChange,
		onColumnVisibilityChange: this.onColumnVisibilityChange,
		onPageIndexChange: this.onPageIndexChange,
		onPageSizeChange: this.onPageSizeChange,
		onFilteringChange: this.onFilteringChange,
		onEditorPreparing: this.onEditorPreparing,
		onEditorCreating: this.onEditorCreating,
		onSummaryChanged: this.onSummaryChanged,
		onEditingStart: this.onEditingStart,
		onEditCanceling: this.onEditCanceling,
		getHeaderFilterData: this.getHeaderFilterData,
		onFilterClear: this.onFilterClear,
		onInitNewRow: this.onInitNewRow,
		onReorder: this.onReorder,
		onForceRefresh: this.onForceRefresh,
		onClearAll: this.onClearAll,
		onExportingAsBase64: this.onExportingAsBase64,
		onColumnFixingChange: this.onColumnFixingChange,
		onCellPrepared: this.onCellPrepared,
		removeSettings: GridSettingsHelper.removeSettings,
		getSettings: GridSettingsHelper.getSettings,
		setSettings: GridSettingsHelper.setSettings
	};
}

function onSortingChange(this: DataGrid, sorting: Array<BCExtremeGridEntities.ISortingColumn>) {
	this.__sorting = sorting;
	this.reload(true);
}

function onSearchingChange(this: DataGrid, value: string) {
	this.__searhcValue = value;
	this.reload(true);
}

function onPageIndexChange(this: DataGrid, pageIndex: number) {
	const tId = this.__form.beginTransaction();

	this.__proxyChanging.paging = {
		...this.paging,
		currentPage: pageIndex + 1
	};
	this.__form.endTransaction(tId).then(() => {
		this.reload(true);
	});
}

function onPageSizeChange(this: DataGrid, pageSize: number) {
	if (!pageSize) {
		return;
	}
	const tId = this.__form.beginTransaction();
	this.__proxyChanging.paging = { ...this.paging, pageSize: pageSize };
	this.__form.endTransaction(tId).then(() => {
		this.reload(true);
	});
}

async function onFilteringChange(this: DataGrid, filterItem: BCExtremeGridEntities.IFilterItem) {
	const tId = this.__form.beginTransaction();

	if (this.__filtering.find(x => x.columnName === filterItem.columnName)) {
		this.__filtering = this.__filtering?.map(filter => {
			if (filter.columnName === filterItem.columnName) {
				return filterItem;
			}
			return filter;
		});
	} else {
		this.__filtering.push(filterItem);
	}
	this.__proxyChanging.paging = { ...this.paging, currentPage: 1 };
	await this.__form.endTransaction(tId);
	this.reload(true);
}

function getDataGridCommonProps(this: DataGrid): BCExtremeGridEntities.IExtremeGridProps {
	let height: number = null;

	if (this.displayType == 'AutoFill') {
		if (this.__form?.windowSize?.height) {
			height = this.__form.windowSize.height - 10;

			if (this.__form.toolbarButtons.filter(t => t.enabled).length) {
				height = height - 75;
			}
		}
	} else if (this.virtualScrolling) {
		height = this.height || 450;
	}

	return {
		disableBatchSave: this.editing.mode === 'batch',
		paging: {
			enabled: !this.virtualScrolling,
			pageSize: this.getPageSize(),
			pageIndex: this.paging.currentPage - 1
		},
		isLoading: this.loading,
		pager: {
			visible: !this.virtualScrolling ? this.paging.enabled : false,
			allowedPageSizes: this.paging.pagingSizes,
			showPageSizeSelector: this.paging.enabled
		},
		sorting: {
			mode: this.sorting.mode
		},
		style: this.style,
		filtering: {
			visible: this.filtering.enabled,
			showOperationChooser: this.filtering.enabled && this.filtering.showOperationChooser,
			applyFilter: 'auto'
		},
		headerFiltering: {
			visible: this.filtering.headerFilterEnabled || false
		},
		searching: {
			visible: this.searching.enabled,
			value: this.__searhcValue
		},
		allowColumnResizing: this.columnSettings.resizeable,
		allowColumnReordering: this.columnSettings.orderable,
		columnResizingMode: this.columnSettings.resizingMode,
		selection: {
			enabled: getSelectionEnabled.call(this) || this.selection.enabled,
			allowSelectAll: this.selection.showSelectAll,
			mode: this.selection.mode,
			showCheckBoxesMode:
				this.selection.showCheckBoxMode || this.selection.showCheckBoxesMode,
			selectAllMode: this.selection.selectAllMode,
			disabledValueColumnName: this.selection.disabledValueColumnName,
			disabledValueMatchers: this.selection.disabledValueMatchers,
			selectedIds: this.selectedRowKeys,
			selectedDatas: this.selectedRowsData
		},
		columnChooser: {
			enabled: this.columnSettings.showColumnChooser,
			allowSearch: false
		},
		editing: {
			enabled: !isNullOrUndefined(this.editing.mode),
			mode: this.editing.mode,
			showAddCommand: this.editing.showAddCommand,
			showDeleteCommand: this.editing.showDeleteCommand,
			showEditCommand: !this.__isReadOnly() && this.editing.showEditCommand
		},
		height,
		virtualScrolling: this.virtualScrolling,
		exportToExcel: this.exportToExcel,
		showClearAllButton: this.showClearAllButton,
		wordWrapEnabled: this.wordWrapEnabled,
		rowAlternationEnabled: this.alternateColor.enabled,
		alternateColor: this.alternateColor.color,
		showColumnLines: this.gridLines == 'vertical' || this.gridLines == 'both',
		showRowLines: this.gridLines == 'horizontal' || this.gridLines == 'both',
		showBorders: this.gridBorder,
		onContentReady: this.noDataTemplateAndSetMaxSize,
		noDataText: findMessage.get('102713'),
		hoverStateEnabled: this.hoverStateEnabled,
		columnHidingEnabled: this.columnHidingEnabled,
		rowDragging: {
			enable: !this.dataSource?.name ? this.rowDragging.enabled : false,
			showDragIcons: !this.dataSource?.name ? this.rowDragging.showDragIcons : false
		}
	};
}

function getToolbarButtons(this: DataGrid): IDictionary<any>[] {
	return this.toolbarActionButtons
		.filter(i => {
			const buttonVisible = isNullOrUndefined(i.visible) || i.visible;

			const hasSelectedRowParam = checkSelectedRowType(i.args);

			const isAllowNull = i.args.parameters?.some(
				parameter => parameter.type == 'selectedRow' && parameter.allowNull
			);

			return buttonVisible && (hasSelectedRowParam || isAllowNull)
				? this.selectedRowKeys.length || isAllowNull
				: buttonVisible;
		})
		.map((item, index) => {
			const text = geti18nValue(item.text, this.__form.currentLanguage);
			const Icon = GetIcon16ByName(item.icon, IconAction);

			return {
				key: index.toString(),
				cacheKey: 'FormDGActBtnCacheKey_' + index,
				title: this.__form?.currentLanguage
					? item.title[this.__form.currentLanguage.culture]
					: this.name,
				text,
				name: item.name,
				iconOnly: isNullOrUndefined(text) || text == '',
				onClick: e => {
					e.stopPropagation();
					e.preventDefault();
					debouncedToolbarItemClickHandler(this, item);
				},
				icon: Icon.info
			};
		});
}

const debouncedToolbarItemClickHandler = debounce((grid: DataGrid, item: any) => {
	onBeforeToolbarItemClick.call(grid, item);
	setTimeout(() => {
		for (const item of Array.from(document.getElementsByClassName('dx-popover-wrapper'))) {
			item?.remove?.();
		}
	}, 10);
}, 300);

function checkSelectedRowType(args: IBaseExecuteActionArgs<ParameterItemType>) {
	return args?.parameters?.some(p => p.type === 'selectedRow');
}

function checkSelectedRowTypeToolbarButton(this: DataGrid) {
	return this.toolbarActionButtons?.some(i => checkSelectedRowType(i.args));
}

function getSelectionEnabled(this: DataGrid) {
	const relatedToolbarButtonExist = checkSelectedRowTypeToolbarButton.call(this);
	return relatedToolbarButtonExist || this.selection.enabled;
}

export {
	getDataGridCommonProps,
	getDataGridProps,
	onFilteringChange,
	onPageIndexChange,
	onPageSizeChange,
	onSearchingChange,
	onSortingChange
};
